import { Feature, GeoJsonProperties } from 'geojson'
import { PathOptions } from 'leaflet'

import {
  MAP_GEOJSON_HOVER_PATH_OPTIONS,
  MAP_GEOJSON_HOVER_WEIGHT_STEP,
  MAP_GEOJSON_PATH_OPTIONS,
  MAP_POINT_FILL_OPACITY
} from 'src/constants/map'

// simplestyle 1.1.0 mapped onto Leaflet's path options. Only these five: `properties` is free, so
// passing anything else through would silently turn every future key into a Leaflet option.
const PATH_KEYS: Record<string, keyof PathOptions> = {
  stroke: 'color',
  'stroke-width': 'weight',
  'stroke-opacity': 'opacity',
  fill: 'fillColor',
  'fill-opacity': 'fillOpacity'
}

// Which of them are numbers. A width that arrives as the string "3" would be added to, not
// counted with: the hover would turn it into "32".
const NUMERIC_KEYS = ['stroke-width', 'stroke-opacity', 'fill-opacity']

/**
 * Does this property carry a value at all?
 *
 * `null`, `undefined` and a blank string are not a zero, they are an absent value. Every place
 * that asks whether the author said something about a feature has to agree on this, otherwise the
 * same document is styled by one rule and not by another: a point whose `fill-opacity` is null
 * would keep the area default, a feature whose style keys are all null would count as styled and
 * take the wrong hover, and a line whose `stroke` is null would be invisible and still catch
 * clicks.
 */
const hasValue = (properties: GeoJsonProperties, key: string): boolean => {
  const raw = properties?.[key]
  if (raw === null || raw === undefined) {
    return false
  }
  return !(typeof raw === 'string' && raw.trim() === '')
}

/**
 * A number from the properties, or nothing at all.
 *
 * `null` and an empty string are not a zero here, they are an absent value: `Number()` turns both
 * into 0, which would read as "the author switched this off" and make the feature invisible and
 * unclickable. A browser discards `stroke-width="null"` as invalid and falls back to its default,
 * and the CMS passes the raw value straight through and lands there too. A QGIS export with an
 * empty cell in an existing column produces exactly this.
 */
const toNumber = (raw: unknown): number | undefined => {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : undefined
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

// Marks the point drawn in place of a project that has nothing visible left. It rides in the
// feature's properties so the stand-in goes through the same code path as everything else.
export const STAND_IN_KEY = '__standIn'

// simplestyle's marker colour. The CMS reads it on a Point; here it also colours the circles of a
// MultiPoint, which Leaflet builds the same way and which would otherwise be the only points on
// the map without their own colour.
const MARKER_COLOR_KEY = 'marker-color'

const propertiesOf = (feature?: Feature): GeoJsonProperties => feature?.properties ?? {}

// MultiPoint counts too: Leaflet builds one circle per position, and each of them needs the fill
// and the hit area a single point gets.
const isPoint = (feature?: Feature): boolean => {
  const type = feature?.geometry?.type
  return type === 'Point' || type === 'MultiPoint'
}

// What an overlay carrier names. The key alone says nothing: the value decides whether there is a
// picture to draw, and what kind of value it is decides whether a browser can load it.
const IMAGE_KEY = 'image'

/**
 * The picture a feature carries, as an address an `<img>` can load, or nothing.
 *
 * `properties.image` is not always an address. The server replaces the id of an overlay belonging
 * to this project with an absolute one and leaves every other value exactly as it stands: an id it
 * could not resolve, a `data:` uri that arrived with an import, whatever an older write path put
 * there. Handing such a value to an `<img>` would draw nothing and still count the feature as
 * drawn, and the project would lose the point that stands in for it.
 *
 * Parsed rather than matched against a pattern. A pattern over the string accepts shapes no
 * browser resolves and rejects addresses that are perfectly good, and it has to be kept in step
 * with every spelling. Parsing also settles the ids for free: without a base they are a relative
 * reference and do not parse at all.
 *
 * Only `http` and `https`. `data:` and `blob:` would render, but they are not what the server
 * promises to deliver, and letting them through here would carry bytes from the document itself
 * into the page rather than a file the server has checked.
 */
export const overlayImageUrl = (feature?: Feature): string | undefined => {
  const raw = propertiesOf(feature)?.[IMAGE_KEY]
  if (typeof raw !== 'string' || raw.trim() === '') {
    return undefined
  }

  try {
    const { protocol } = new URL(raw)
    return protocol === 'http:' || protocol === 'https:' ? raw : undefined
  } catch {
    return undefined
  }
}

/**
 * Does this feature say anything about how it looks?
 *
 * One that says nothing is drawn in the colours of the instance and takes the shared hover, which
 * is what nearly every project on the map gets: their documents carry empty properties.
 */
export const hasOwnStyle = (feature?: Feature): boolean => {
  const properties = propertiesOf(feature) ?? {}
  const styleKeys = isPoint(feature) ? [...Object.keys(PATH_KEYS), MARKER_COLOR_KEY] : Object.keys(PATH_KEYS)
  return styleKeys.some((key) => hasValue(properties, key))
}

/**
 * The feature's own style, each field falling back on its own.
 *
 * Presence, not truth: `stroke-width: 0` means "no line" and `fill-opacity: 0` means "no fill".
 * Reading them with `||` would treat both as missing and draw the default, which is exactly what
 * the author hid, and authors do use both. For a real zero the CMS map reads them the same way.
 * It parts company on an absent value, which it hands to Leaflet as it stands; see `hasValue`.
 */
export const featurePathOptions = (feature?: Feature): PathOptions => {
  const properties = propertiesOf(feature) ?? {}
  const style: PathOptions = { ...MAP_GEOJSON_PATH_OPTIONS }

  Object.entries(PATH_KEYS).forEach(([key, option]) => {
    if (hasValue(properties, key)) {
      if (NUMERIC_KEYS.includes(key)) {
        const value = toNumber(properties[key])
        if (value !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (style as any)[option] = value
        }
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (style as any)[option] = properties[key]
    }
  })

  if (isPoint(feature)) {
    if (hasValue(properties, MARKER_COLOR_KEY)) {
      style.color = properties[MARKER_COLOR_KEY]
      style.fillColor = properties[MARKER_COLOR_KEY]
    }
    // A point carries the fill the CMS map gives it, which is a little denser than the one an
    // area gets here. It belongs in this function and not where the circle is built: Leaflet runs
    // resetStyle over every layer right after pointToLayer, so a fill set on the marker would be
    // overwritten by whatever this function returns.
    if (!hasValue(properties, 'fill-opacity')) {
      style.fillOpacity = MAP_POINT_FILL_OPACITY
    }
  }

  return style
}

/**
 * The hover style. A feature without its own style takes the shared hover colours; one with its
 * own keeps its colours and only gets a thicker line.
 *
 * The line carries the signal because the fill often cannot: a polygon that is already opaque and
 * a line without any fill would both look unchanged. And a line the author switched off stays off,
 * otherwise the hover would show exactly what they hid.
 */
export const featureHoverOptions = (feature?: Feature): PathOptions => {
  // The stand-in carries a style only so it can run through the same function as everything else.
  // On hover it should behave like a feature without one: the fill changes colour and the ring
  // stays put. Two more units of weight would grow that ring inwards and make the point look
  // smaller, and the ring is nearly invisible against the base map anyway.
  if (propertiesOf(feature)?.[STAND_IN_KEY]) {
    return { ...featurePathOptions(feature), fillColor: MAP_GEOJSON_HOVER_PATH_OPTIONS.fillColor }
  }

  if (!hasOwnStyle(feature)) {
    return MAP_GEOJSON_HOVER_PATH_OPTIONS
  }

  const style = featurePathOptions(feature)
  const lineIsVisible = (style.opacity ?? 1) > 0 && (style.weight ?? 0) > 0

  return lineIsVisible
    ? { ...style, weight: (style.weight ?? 0) + MAP_GEOJSON_HOVER_WEIGHT_STEP }
    : style
}

// Which geometries have an area that could carry a click. A line has none: switch its stroke off
// and nothing is left to catch anything.
const FILLED_TYPES = ['Polygon', 'MultiPolygon', 'Point', 'MultiPoint', 'GeometryCollection']

/**
 * Can this feature take a click?
 *
 * A feature the author hid, with no visible stroke and no visible fill, would otherwise keep its
 * invisible hit area, and a click would land on something nobody can see.
 *
 * A GeometryCollection counts as fillable even though it may hold only lines. The wrong call in
 * that direction leaves one hit area too many; the other way round it swallows a click.
 */
export const isFeatureCatchable = (feature?: Feature): boolean => {
  // A carrier is answered before its style is read at all. What a visitor sees there is the
  // picture over the box, and the carrier's own style says nothing about that: it describes a
  // rectangle no map draws. An author is free to switch its stroke and fill off, and weighing
  // those values here would drop exactly those carriers out of the hit layer.
  if (overlayImageUrl(feature)) {
    return true
  }

  const style = featurePathOptions(feature)

  if ((style.opacity ?? 1) > 0 && (style.weight ?? 0) > 0) {
    return true
  }

  const type = feature?.geometry?.type
  if (!type || !FILLED_TYPES.includes(type)) {
    return false
  }

  return (style.fillOpacity ?? 0) > 0
}

/**
 * Does the zoom lie inside the range this feature's author gave it?
 *
 * Both ends are inclusive, so `minzoom: 15` with `maxzoom: 16` is drawn on 15 and on 16, and
 * `minzoom` above `maxzoom` is drawn nowhere. Counted in Leaflet zoom levels, the scale this map
 * works in.
 *
 * Every map applies both ends, the overview as well as a project's own, and a shape and a picture
 * answer to the same range. That is why this stands on its own rather than inside the question
 * about shapes: two copies of the comparison would let the two drift apart, and a picture that
 * outlives its `maxzoom` looks like a picture the range was never meant for.
 *
 * Whether anything is left to see when zoomed out is the editors' call; where nothing is, the map
 * shows the stand-in point instead.
 */
export const isWithinZoom = (properties: GeoJsonProperties, zoom: number): boolean => {
  // `toNumber`, not `Number`: an author who leaves the field empty writes null or "", and `Number`
  // turns both into 0. On `maxzoom` that 0 is a real bound and would hide the feature everywhere
  // above it; measured against zoom 12 to 16, null, "", "   ", true and false each left nothing
  // visible at all.
  const minzoom = toNumber(properties?.minzoom)
  if (minzoom !== undefined && zoom < minzoom) {
    return false
  }

  const maxzoom = toNumber(properties?.maxzoom)
  if (maxzoom !== undefined && zoom > maxzoom) {
    return false
  }

  return true
}

/**
 * Is this feature drawn as a shape at this zoom?
 *
 * Three reasons not to draw it: it has no geometry at all, it names an `image`, which puts a
 * picture in the place of its outline, or the zoom lies outside the range its author gave it. The
 * middle one is read as a value, not as a key: an `image: null` says the author left the field
 * empty, and hiding the shape for that would empty a whole document.
 *
 * Every carrier is kept out, however it came about. One may be a rectangle drawn for the picture
 * and another an area that was already there and had a picture put on it; either way the picture
 * covers the outline, and the editor's own map leaves it out for the same reason. It is kept out
 * whatever the `image` holds too, a value no browser can load included: the outline does not come
 * back because the picture is missing. Whether there is a picture instead is `overlayImageUrl`.
 *
 * Takes the raw object, not a Feature: Leaflet hands `filter` what it was given, before wrapping a
 * bare geometry into a feature, and a bare geometry has no `properties` at all. Bare geometries
 * are what an instance delivers until its documents have been saved once.
 */
export const isFeatureVisible = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  feature: any,
  zoom: number
): boolean => {
  const properties = feature?.properties ?? {}

  // RFC 7946 allows a null geometry. Leaflet skips such a feature in addData, so counting it as
  // visible would keep a project from getting its stand-in point.
  if (feature && 'geometry' in feature && !feature.geometry) {
    return false
  }

  if (hasValue(properties, IMAGE_KEY)) {
    return false
  }

  return isWithinZoom(properties, zoom)
}
